import React, { useState, useRef, useEffect } from "react";
import { patch } from "@rails/request.js";
import { motion } from "framer-motion";

import { Toasts, useToasts } from "./../shared/Toast";
import EditableName from "./../shared/EditableName";
import ViewControls from "./../shared/ViewControls";
import FullscreenPreview from "./../shared/FullscreenPreview";
import OptionalColor from "./../shared/OptionalColor";
import ColorField from "./../shared/ColorField";
import WallShow from "./Show";
import { MANIFESTS } from "./public/manifest";
import { compatibilityCount } from "./../widget/public/manifest";
import RadioButtons from "./../shared/RadioButtons";
import { WallHandPicked } from "./HandPicked";

// Wall studio, the same shell as the capform, survey and widget editors:
// chromeless studio header (back link, editable name, view controls,
// share) with an accordion sidebar beside a live preview.
//
// The preview mounts the real WallShow component, so what you see here is
// the component the public page renders, not a mock of it. WallShow fetches
// its own proofs from /api/v1/walls/:uuid/proofs, which reads saved state,
// so theme, colours and intro update as you type while the proof list
// refreshes on save.

const KIND_SKETCH = {
  cards: "▢▢▢", bubbles: "💬", chats: "📱", futuristic: "✦",
  quotes: "❝", masonry: "▦", tweets: "𝕏", tabbed: "▤",
  press: "📰", gallery: "🖼", audio: "🎧", reels: "▶",
};

const SECTIONS = [
  { id: "hero",    label: "hero",    color: "text-ssblue",   accent: "#3E8DFF" },
  { id: "theme",   label: "theme",   color: "text-sspink",   accent: "#FF7AAC" },
  { id: "proofs",  label: "proofs",  color: "text-ssgreen",  accent: "#3EB567" },
  { id: "design",  label: "design",  color: "text-ssorange", accent: "#FF8F47" },
];

const DESIGN_DEFAULTS = { bg_color: "#FAFAF7", primary_color: "#FF7AAC", intro: "" };

export default function WallEdit({
  project_id, wall_id, init_wall, project_labels = [], available_proofs = [],
  approved_label_counts = {}, back_url,
}) {
  const [wall, setWall] = useState(init_wall.wall);
  const [section, setSection] = useState("hero");
  const [previewMode, setPreviewMode] = useState("desktop");
  const [breakoutOpen, setBreakoutOpen] = useState(false);
  const [saveSpin, setSaveSpin] = useState(false);
  // Bumped after every successful save so the preview remounts and
  // re-fetches. Hand-picked ids and label filters resolve server side, so
  // they cannot update from local state alone.
  const [previewNonce, setPreviewNonce] = useState(0);
  const { toasts, setToasts, addToast } = useToasts();

  const backUrl  = back_url || `/projects/${project_id}/walls`;
  const shareUrl = `/projects/${project_id}/walls/${wall.id}/share`;
  const publicUrl = `/walls/${wall.uuid}`;

  // Saves coalesce the way the survey studio's do: tabbing through a panel
  // gives one "Saved" rather than one per field. Failures never coalesce.
  const savedTimer = useRef(null);
  const notifySaved = () => {
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => {
      savedTimer.current = null;
      addToast("success", "Saved");
    }, 600);
  };
  useEffect(() => () => { if (savedTimer.current) clearTimeout(savedTimer.current); }, []);

  const patchWall = async (attrs) => {
    setSaveSpin(true);
    const response = await patch(`/api/v1/walls/${wall.id}`, {
      body: { wall: attrs },
      contentType: "application/json", responseKind: "json",
    });
    setSaveSpin(false);
    if (response.ok) {
      const json = await response.json;
      setWall(json.wall);
      setPreviewNonce((n) => n + 1);
      notifySaved();
      return json.wall;
    }
    addToast("error", "Couldn't save");
    return null;
  };

  const setDesign = (patchAttrs) =>
    patchWall({ design: { ...DESIGN_DEFAULTS, ...(wall.design || {}), ...patchAttrs } });

  // What the preview renders. Design and theme come from local state so they
  // update immediately; the proof list comes from the server on the nonce.
  const previewWall = {
    wall: {
      id: wall.id, uuid: wall.uuid, name: wall.name, kind: wall.kind, slug: wall.slug,
      automatic: wall.automatic, proof_ids: wall.proof_ids,
      bg_color: wall.bg_color, primary_color: wall.primary_color,
      intro: wall.intro, design: wall.design || {},
    },
  };

  return (
    <div className="h-full flex flex-col">
      <Toasts toasts={toasts} setToasts={setToasts} />

      <header className="flex-none border-b-[3px] border-black bg-white">
        <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2.5">
          <a href={backUrl}
             className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider bg-white border-[2px] border-black text-slate-700 hover:bg-ssyellow hover:text-slate-900 shadow-[2px_2px_0_rgba(0,0,0,0.25)] hover:shadow-[3px_3px_0_rgba(0,0,0,0.35)] hover:-translate-y-0.5 transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="h-3.5 w-3.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
            <span className="hidden sm:inline">back to walls</span>
            <span className="sm:hidden">back</span>
          </a>

          <div className="flex-1 min-w-0 flex items-center gap-2">
            <span className="zz-hand text-xl text-slate-400 hidden sm:inline">editing</span>
            <EditableName
              endpoint={`/api/v1/walls/${wall.id}`}
              resourceKey="wall"
              name={wall.name}
              fallback="untitled wall"
              onChange={(name) => setWall((w) => ({ ...w, name }))}
              onSuccess={() => notifySaved()}
              onError={(msg) => addToast("error", msg || "Couldn't save the name")}
            />
          </div>

          <div className="flex-none flex items-center gap-1.5 sm:gap-2">
            <ViewControls
              mode={previewMode}
              setMode={setPreviewMode}
              onBreakout={() => setBreakoutOpen(true)}
              breakoutActive={breakoutOpen}
            />
            {saveSpin && (
              <svg className="animate-spin h-4 w-4 text-slate-500" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            )}
            <a href={shareUrl} data-turbo="false" className="zz-btn sm green">share</a>
          </div>
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="flex flex-col md:flex-row gap-4 sm:gap-5 p-3 sm:p-5">
          <aside className="w-full md:w-1/2 md:flex-none">
            <div className="bg-white border-[3px] border-black md:h-[calc(100vh-160px)] md:overflow-y-auto">
              <div className="divide-y-[3px] divide-black">
                {SECTIONS.map((s) => {
                  const isOpen = section === s.id;
                  return (
                    <div key={s.id}>
                      <button
                        type="button"
                        onClick={() => setSection(isOpen ? null : s.id)}
                        aria-expanded={isOpen}
                        className={`w-full px-5 py-4 flex justify-between items-center text-left transition-colors ${isOpen ? "bg-slate-50" : "hover:bg-slate-50"}`}
                      >
                        <span className={`zz-hand text-2xl ${s.color}`}>{s.label}</span>
                        <span className={`text-xl font-black transition-transform ${isOpen ? "rotate-90" : ""}`}
                              style={{ color: s.accent }} aria-hidden="true">›</span>
                      </button>
                      <motion.div
                        initial={false}
                        animate={{ height: isOpen ? "auto" : 0, display: isOpen ? "block" : "none", opacity: isOpen ? 1 : 0 }}
                        className="relative z-20 overflow-hidden"
                      >
                        {s.id === "theme"  && <ThemePanel  wall={wall} onChange={patchWall} availableProofs={available_proofs} />}
                        {s.id === "hero"   && <HeroPanel   wall={wall} onChange={setDesign} />}
                        {s.id === "proofs" && <ProofsPanel wall={wall} onChange={patchWall}
                                                           projectId={project_id}
                                                           projectLabels={project_labels}
                                                           availableProofs={available_proofs}
                                                           approvedLabelCounts={approved_label_counts} />}
                        {s.id === "design" && <DesignPanel wall={wall} onChange={setDesign} />}
                      </motion.div>
                    </div>
                  );
                })}
              </div>
            </div>
          </aside>

          <section className="w-full md:flex-1 min-w-0">
            <div className="bg-white border-[3px] border-black md:h-[calc(100vh-160px)] overflow-hidden flex flex-col">
              {/* Same browser chrome the capform and survey previews use, so
                  all the editors frame their preview the same way. */}
              <div className="flex items-center gap-1.5 px-3 py-2 border-b-[2px] border-black bg-slate-100 flex-none">
                <span className="block w-2.5 h-2.5 rounded-full bg-[#FF7AAC] border border-black/40" />
                <span className="block w-2.5 h-2.5 rounded-full bg-[#FFD23A] border border-black/40" />
                <span className="block w-2.5 h-2.5 rounded-full bg-[#3EB567] border border-black/40" />
                <div className="ml-3 flex-1 h-5 bg-white border border-black/20 rounded-sm flex items-center px-2">
                  <span className="text-[10px] font-bold text-slate-400 truncate">your-site.com</span>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto bg-slate-100 flex justify-center">
                <div className={previewMode === "mobile" ? "w-[390px] shrink-0 bg-white" : "w-full bg-white"}>
                  <WallShow key={previewNonce} initWall={previewWall} />
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {breakoutOpen && (
        <FullscreenPreview
          url={publicUrl}
          title="Wall fullscreen preview"
          onClose={() => setBreakoutOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Panels ──────────────────────────────────────────────────────────

// The hero sits above the proofs on every theme, rendered once in WallChrome.
// All four fields live in the design jsonb; each is optional and an empty one
// collapses. hero_headline falls back to the older `intro` value at render
// time, which is why the headline input seeds from it.
function HeroPanel({ wall, onChange }) {
  const design = wall.design || {};
  const headline = design.hero_headline ?? wall.intro ?? "";
  const enabled = design.hero_enabled !== false;

  return (
    <div className="flex flex-col gap-4 p-4">
      <div>
        <span className="zz-label">Hero section</span>
        <div className="mt-1">
          <RadioButtons
            kind="hero"
            buttons={["Show hero", "No hero"]}
            selected={enabled ? "Show hero" : "No hero"}
            setSelected={(val) => onChange({ hero_enabled: val === "Show hero" })}
          />
        </div>
        <span className="block text-[11px] text-slate-500 mt-1.5">
          {enabled
            ? "The headline, subtext and button show above the proofs."
            : "The wall starts straight at the proofs. The fields below are kept, just not shown."}
        </span>
      </div>

      <div className={`flex flex-col gap-4 ${enabled ? "" : "opacity-40 pointer-events-none"}`}
           aria-hidden={!enabled}>
      <label className="block">
        <span className="zz-label">Headline</span>
        <input type="text" placeholder="Our Wall of Love"
               defaultValue={headline}
               key={`hero-headline-${headline}`}
               onBlur={(e) => onChange({ hero_headline: e.target.value })}
               className="mt-1 w-full px-3 py-2 text-base border-[2px] border-black rounded bg-white focus:outline-none focus:ring-2 focus:ring-ssblue" />
        <span className="block text-[11px] text-slate-500 mt-1">
          The big H1 at the top of the page.
        </span>
      </label>

      <label className="block">
        <span className="zz-label">Subtext</span>
        <textarea rows="3" placeholder="We are loved by startups, agencies and many more."
                  defaultValue={design.hero_subtext || ""}
                  key={`hero-subtext-${design.hero_subtext}`}
                  onBlur={(e) => onChange({ hero_subtext: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-base border-[2px] border-black rounded bg-white focus:outline-none focus:ring-2 focus:ring-ssblue" />
      </label>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="zz-label">Button label</span>
          <input type="text" placeholder="Create your own wall"
                 defaultValue={design.hero_cta_label || ""}
                 key={`hero-cta-label-${design.hero_cta_label}`}
                 onBlur={(e) => onChange({ hero_cta_label: e.target.value })}
                 className="mt-1 w-full px-3 py-2 text-base border-[2px] border-black rounded bg-white focus:outline-none focus:ring-2 focus:ring-ssblue" />
        </label>
        <label className="block">
          <span className="zz-label">Button link</span>
          <input type="url" placeholder="https://example.com/signup"
                 defaultValue={design.hero_cta_url || ""}
                 key={`hero-cta-url-${design.hero_cta_url}`}
                 onBlur={(e) => onChange({ hero_cta_url: e.target.value })}
                 className="mt-1 w-full px-3 py-2 text-base border-[2px] border-black rounded bg-white focus:outline-none focus:ring-2 focus:ring-ssblue" />
        </label>
      </div>
      <p className="text-[11px] text-slate-500 -mt-1">
        The button only appears when both a label and a link are set. It uses
        your primary colour and opens in a new tab.
      </p>
      </div>
    </div>
  );
}

function ThemePanel({ wall, onChange, availableProofs = [] }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4">
      {MANIFESTS.map((manifest) => {
        const { key, name, description, supportsInfiniteScroll } = manifest;
        const on = wall.kind === key;
        const compatible = compatibilityCount(manifest, availableProofs).compatible;
        return (
          <button key={key} type="button" onClick={() => onChange({ kind: key })}
                  aria-pressed={on}
                  title={description}
                  style={on ? { backgroundColor: "#FF7AAC", color: "#fff" } : undefined}
                  className={`zz-card tight p-4 text-center transition-all hover:-translate-y-0.5 ${on ? "border-black shadow-[5px_5px_0_#111]" : ""}`}>
            <div className="text-2xl mb-1">{KIND_SKETCH[key] || "▢"}</div>
            <div className="font-black text-sm">{name}</div>
            <div className={`mt-1.5 text-[10px] font-bold uppercase tracking-wider ${on ? "text-white/80" : compatible === 0 ? "text-ssred" : "text-slate-500"}`}>
              {compatible === 0
                ? "no compatible proofs"
                : `${compatible} compatible proof${compatible === 1 ? "" : "s"}`}
            </div>
            <div className={`mt-1 text-[10px] font-bold ${on ? "text-white/70" : "text-slate-400"}`}>
              {supportsInfiniteScroll ? "∞ infinite scroll" : "single page"}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function ProofsPanel({ wall, onChange, projectId, projectLabels, availableProofs, approvedLabelCounts }) {
  const handPicked = wall.automatic === "Hand Picked";
  const selectedLabelIds = wall.filter_label_ids || [];

  const toggleLabel = (id) => {
    const next = selectedLabelIds.includes(id)
      ? selectedLabelIds.filter((x) => x !== id)
      : [ ...selectedLabelIds, id ];
    onChange({ filter_label_ids: next.length ? next : [ "" ] });
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex gap-3">
        {[ "Automatic", "Hand Picked" ].map((mode) => {
          const on = wall.automatic === mode;
          return (
            <button key={mode} type="button"
                    aria-pressed={on}
                    onClick={() => onChange({ selection: { automatic: mode, proof_ids: wall.proof_ids || [] } })}
                    style={on ? { backgroundColor: "#3EB567", color: "#fff" } : undefined}
                    className={`flex-1 zz-card tight p-3 text-center transition-all ${on ? "shadow-[4px_4px_0_#111]" : ""}`}>
              <div className="font-black text-sm">{mode}</div>
              <div className="text-[11px] mt-0.5 opacity-80">
                {mode === "Automatic" ? "all approved proofs, latest first" : "pick the ones to include"}
              </div>
            </button>
          );
        })}
      </div>

      {handPicked ? (
        availableProofs.length === 0 ? (
          <p className="text-sm text-slate-500 p-4 text-center border-[2px] border-dashed border-slate-300 rounded">
            No approved proofs yet. Go to <a href={`/projects/${projectId}/proofs`} className="zz-link">proofs</a> first.
          </p>
        ) : (
          <WallHandPicked
            availableProofs={availableProofs}
            initSelectedIds={wall.proof_ids || []}
            onChange={(ids) => onChange({ selection: { automatic: "Hand Picked", proof_ids: ids } })}
          />
        )
      ) : (
        <div className="flex flex-col gap-4">
          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox"
                   checked={!!wall.require_avatar}
                   onChange={(e) => onChange({ selection: { automatic: "Automatic", require_avatar: e.target.checked, proof_ids: wall.proof_ids || [] } })}
                   className="mt-0.5 h-4 w-4 accent-ssgreen" />
            <span>
              <span className="font-bold text-sm">Require an avatar</span>
              <span className="block text-[11px] text-slate-500">
                Only show proofs whose contact has a picture. Keeps a wall from
                filling with blank circles.
              </span>
            </span>
          </label>

          <div>
          <p className="zz-label mb-2">Filter by label</p>
          {projectLabels.length === 0 ? (
            <p className="text-xs text-slate-500 italic">
              No labels in this project yet. Add some on the{" "}
              <a href={`/projects/${projectId}/labels`} className="zz-link">labels page</a>.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              <button type="button" onClick={() => onChange({ filter_label_ids: [ "" ] })}
                      className={`px-2.5 py-1 text-xs font-bold rounded-full border-[2px] border-black transition-colors ${selectedLabelIds.length === 0 ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-100"}`}>
                all approved proofs
              </button>
              {projectLabels.map((label) => {
                const on = selectedLabelIds.includes(label.id);
                const count = approvedLabelCounts[label.id] || 0;
                return (
                  <button key={label.id} type="button" onClick={() => toggleLabel(label.id)}
                          title={count === 0 ? "No approved proofs carry this label yet" : `${count} approved proof(s)`}
                          className={`px-2.5 py-1 text-xs font-bold rounded-full border-[2px] border-black transition-colors inline-flex items-center gap-1 ${on ? "text-white" : "bg-white text-slate-700 hover:bg-slate-100"} ${count === 0 && !on ? "opacity-50" : ""}`}
                          style={on ? { backgroundColor: label.color } : undefined}>
                    <span>{label.name}</span>
                    <span className={`text-[10px] ${on ? "text-white/80" : "text-slate-400"}`}>({count})</span>
                  </button>
                );
              })}
            </div>
          )}
          </div>
        </div>
      )}
    </div>
  );
}

function DesignPanel({ wall, onChange }) {
  const design = wall.design || {};
  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center justify-between gap-3">
        <p className="zz-label">Colours</p>
        <button type="button" onClick={() => onChange(DESIGN_DEFAULTS)}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-wider bg-white border-[2px] border-black text-slate-700 hover:bg-ssyellow transition-colors rounded">
          ↺ reset to defaults
        </button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <span className="zz-label">Background</span>
          <ColorField value={design.bg_color || DESIGN_DEFAULTS.bg_color}
                      onChange={(v) => onChange({ bg_color: v })} />
        </div>
        <div>
          <span className="zz-label">Primary / accent</span>
          <ColorField value={design.primary_color || DESIGN_DEFAULTS.primary_color}
                      onChange={(v) => onChange({ primary_color: v })} />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <OptionalColor label="Text" value={design.text_color}
                       onChange={(v) => onChange({ text_color: v })} />
        <OptionalColor label="Card background" value={design.card_bg_color}
                       onChange={(v) => onChange({ card_bg_color: v })} />
        <OptionalColor label="Highlight" value={design.highlight_color}
                       onChange={(v) => onChange({ highlight_color: v })} />
        <OptionalColor label="Highlight text" value={design.highlight_text_color}
                       onChange={(v) => onChange({ highlight_text_color: v })} />
      </div>

      <label className="flex items-start gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={design.show_dates !== false}
          onChange={(e) => onChange({ show_dates: e.target.checked })}
          className="mt-0.5 h-4 w-4 accent-ssblue"
        />
        <span>
          <span className="font-bold text-sm">Show dates</span>
          <span className="block text-[11px] text-slate-500">
            Dates are not compatible with all widgets.
          </span>
        </span>
      </label>


    </div>
  );
}
